import { chests as fetchChests, openChest as applyChest } from '@/net/api';
import type { Quality } from '@/pixi/quality';

import { arrivalStore, endArrival } from './arrival-state';
import { ChestStage } from './chest-stage';
import { noteHeld, refreshChests } from './chests-state';

/**
 * Blockari's ArrivalLayer (screens/ChestArrival.tsx): mounted once, it plays
 * each batch `showArrival` starts — over the results screen or home — and
 * fetches the batch's stored rolls as it starts (the GET never opens a chest:
 * LATER keeps it unopened; TAP TO OPEN applies it as the reveal starts).
 */
export function mountArrivalLayer(opts: {
  quality: () => Quality;
  /** The balance may have moved (a chest's $CHAIN): refresh the account. */
  onBalance: () => void;
}): () => void {
  let stage: ChestStage | null = null;
  let key = -1;
  const close = (): void => {
    stage?.destroy();
    stage = null;
    key = -1;
  };
  const unsubscribe = arrivalStore.subscribe((s) => {
    const batch = s.batch;
    if (!batch) {
      close();
      return;
    }
    if (batch.key === key) return;
    close();
    key = batch.key;
    const dev = batch.dev;
    const current = new ChestStage({
      chests: batch.chests,
      arrival: true,
      dev,
      quality: opts.quality(),
      onFinish: () => {
        endArrival();
        if (!dev) void refreshChests();
        opts.onBalance();
      },
      onClose: () => endArrival(),
      onOpen: (id) => {
        if (dev) return;
        // A guest's open only HOLDS the chest: it now counts as held, not unopened.
        void applyChest(id).then((r) => {
          if (r.ok && r.value.held) noteHeld(r.value.chest);
        });
      },
      onBalance: () => {
        if (!dev) opts.onBalance();
      },
    });
    stage = current;
    if (!dev) {
      void fetchChests().then((r) => {
        if (!r.ok || stage !== current) return;
        for (const c of batch.chests) {
          const view = r.value.chests.find((v) => v.id === c.id);
          if (view) current.setRoll(c.id, view);
        }
      });
    }
  });
  return () => {
    unsubscribe();
    close();
  };
}
